import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { useBuilderStore } from '../../stores/builderStore';
import { EnhancedComponentLibrary } from './EnhancedComponentLibrary';
import { Canvas } from './Canvas';
import { PropertyEditor } from '../PropertyEditor';
import { Preview } from '../Preview';
import {
  Save,
  Undo,
  Redo,
  Eye,
  Smartphone,
  Tablet,
  Monitor,
  Plus,
  Settings,
  Upload,
  Download,
  RefreshCw,
  Check,
  X,
  Image
} from 'lucide-react';
import toast from 'react-hot-toast';
import { debounce } from 'lodash-es';
import { AssetManager } from '../AssetManager';

export const PageBuilder: React.FC = () => {
  // Load sidebar preference from localStorage, default to true
  const [showLibrary, setShowLibrary] = useState(() => {
    const saved = localStorage.getItem('pagebuilder-sidebar-visible');
    return saved !== null ? saved === 'true' : true;
  });
  const [showPreview, setShowPreview] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showAssetManager, setShowAssetManager] = useState(false);

  const {
    sections,
    selectedId,
    isDragging,
    isSaving,
    hasChanges,
    activeDevice,
    history,
    moveSection,
    selectSection,
    setIsDragging,
    setActiveDevice,
    undo,
    redo,
    save,
    publish,
    loadSections,
    loadTemplates
  } = useBuilderStore();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load initial data
  useEffect(() => {
    loadCurrentHomepage();
    loadTemplates();
  }, []);

  // Save sidebar preference to localStorage
  useEffect(() => {
    localStorage.setItem('pagebuilder-sidebar-visible', showLibrary.toString());
  }, [showLibrary]);

  // Auto-save with stable reference
  const debouncedSaveRef = useRef<any>(null);

  // Create debounced save function only once
  useEffect(() => {
    debouncedSaveRef.current = debounce(async () => {
      // Access current values directly from the store
      const currentState = useBuilderStore.getState();
      if (currentState.hasChanges && !currentState.isSaving) {
        try {
          await currentState.save();
          toast.success('Auto-saved', { duration: 1000, icon: '💾' });
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }
    }, 3000);

    // Cleanup on unmount
    return () => {
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
    };
  }, []); // Empty dependency array - created only once

  // Trigger auto-save when sections change
  useEffect(() => {
    if (hasChanges && debouncedSaveRef.current) {
      debouncedSaveRef.current();
    }
  }, [sections, hasChanges]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Toggle sidebar: Cmd/Ctrl + B
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setShowLibrary(prev => !prev);
      }
      // Save: Cmd/Ctrl + S
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      // Undo: Cmd/Ctrl + Z
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Redo: Cmd/Ctrl + Shift + Z
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        redo();
      }
      // Preview: Cmd/Ctrl + P
      if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
        e.preventDefault();
        setShowPreview(!showPreview);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPreview]);

  const loadCurrentHomepage = async () => {
    try {
      console.log('Loading homepage from API...');
      const response = await fetch('https://clarityofconcept.com/api/homepage/current');

      if (response.ok) {
        const data = await response.json();
        console.log('API Response:', data);
        console.log('API Response sections:', data.sections?.map((s: any) => ({
          id: s.id,
          type: s.type,
          section_type: s.section_type,
          title: s.title
        })));

        if (data.sections && data.sections.length > 0) {
          // The store's loadSections will handle the transformation
          loadSections(data.sections);
          toast.success(`Loaded ${data.sections.length} sections`, {
            duration: 2000,
            icon: '📥'
          });
        } else {
          console.log('No sections found in homepage data');
          toast('Starting with a blank homepage', { duration: 2000, icon: 'ℹ️' });
        }
      } else {
        console.error('Failed to fetch homepage:', response.status);
        toast.error(`Failed to load homepage: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to load homepage:', error);
      toast.error('Failed to load homepage - check network connection');
    }
  };

  const handleSave = async () => {
    try {
      console.log('Saving homepage with sections:', sections.length);
      await save();
      toast.success(`${sections.length} sections saved successfully!`, {
        duration: 3000,
        icon: '✅'
      });
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save changes');
    }
  };

  const handlePublish = async () => {
    if (!confirm('Are you sure you want to publish these changes? They will be live immediately.')) {
      return;
    }

    try {
      await publish();
      toast.success('Homepage published successfully!', {
        duration: 5000,
        icon: '🚀'
      });
    } catch (error) {
      toast.error('Failed to publish homepage');
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && over) {
      const oldIndex = sections.findIndex(s => s.id === active.id);
      const newIndex = sections.findIndex(s => s.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        moveSection(oldIndex, newIndex);
      }
    }

    setActiveId(null);
    setIsDragging(false);
  };

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Panel - Component Library */}
      {showLibrary && (
        <EnhancedComponentLibrary onClose={() => setShowLibrary(false)} />
      )}

      {/* Center - Canvas or Preview */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        <div className="bg-white border-b px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left side */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowLibrary(!showLibrary)}
                className={`px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2 ${
                  !showLibrary ? 'bg-blue-50 text-blue-600' : ''
                }`}
                title={showLibrary ? 'Hide Component Library' : 'Show Component Library (⌘+B)'}
              >
                <Plus className="w-5 h-5" />
                {!showLibrary && <span className="text-sm font-medium">Components</span>}
              </button>

              <div className="w-px h-6 bg-gray-300 mx-1" />

              <button
                onClick={undo}
                disabled={!canUndo}
                className={`p-2 rounded-lg transition-colors ${
                  canUndo ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                }`}
                title="Undo (Cmd+Z)"
              >
                <Undo className="w-5 h-5" />
              </button>

              <button
                onClick={redo}
                disabled={!canRedo}
                className={`p-2 rounded-lg transition-colors ${
                  canRedo ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                }`}
                title="Redo (Cmd+Shift+Z)"
              >
                <Redo className="w-5 h-5" />
              </button>

              <div className="w-px h-6 bg-gray-300 mx-1" />

              {/* Device toggles */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveDevice('desktop')}
                  className={`p-2 rounded ${
                    activeDevice === 'desktop' ? 'bg-white shadow' : ''
                  }`}
                  title="Desktop View"
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setActiveDevice('tablet')}
                  className={`p-2 rounded ${
                    activeDevice === 'tablet' ? 'bg-white shadow' : ''
                  }`}
                  title="Tablet View"
                >
                  <Tablet className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setActiveDevice('mobile')}
                  className={`p-2 rounded ${
                    activeDevice === 'mobile' ? 'bg-white shadow' : ''
                  }`}
                  title="Mobile View"
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Center - Status */}
            <div className="flex items-center gap-2">
              {hasChanges && (
                <span className="text-sm text-orange-600 flex items-center gap-1">
                  <span className="w-2 h-2 bg-orange-600 rounded-full animate-pulse" />
                  Unsaved changes
                </span>
              )}
              {isSaving && (
                <span className="text-sm text-blue-600 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Saving...
                </span>
              )}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  showPreview ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                }`}
                title="Preview (Cmd+P)"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>

              <button
                onClick={() => setShowAssetManager(true)}
                className="px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-2 transition-colors"
                title="Asset Manager"
              >
                <Image className="w-4 h-4" />
                Assets
              </button>

              <button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  hasChanges && !isSaving
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                title="Save (Cmd+S)"
              >
                <Save className="w-4 h-4" />
                Save
              </button>

              <button
                onClick={handlePublish}
                className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                title="Publish to live site"
              >
                <Upload className="w-4 h-4" />
                Publish
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 64px)' }}>
          {showPreview ? (
            <Preview device={activeDevice} sections={sections} />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={sections.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <Canvas
                  sections={sections}
                  selectedId={selectedId}
                  onSelectSection={selectSection}
                  device={activeDevice}
                />
              </SortableContext>

              <DragOverlay>
                {activeId ? (
                  <div className="bg-white shadow-lg rounded-lg p-4 opacity-80">
                    <p className="text-sm font-medium">
                      {sections.find(s => s.id === activeId)?.title}
                    </p>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      </div>

      {/* Right Panel - Property Editor */}
      {selectedId && !showPreview && (
        <PropertyEditor
          section={sections.find(s => s.id === selectedId)!}
          onClose={() => selectSection(null)}
        />
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
          <div className="bg-white rounded-lg w-[90%] h-[90%] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              <Preview
                sections={sections.filter(s => s.is_visible)}
                device={activeDevice}
              />
            </div>
          </div>
        </div>
      )}

      {/* Asset Manager Modal */}
      {showAssetManager && (
        <AssetManager
          isOpen={showAssetManager}
          onClose={() => setShowAssetManager(false)}
          onSelect={(asset) => {
            // Copy URL to clipboard
            navigator.clipboard.writeText(asset.url);
            toast.success('Image URL copied to clipboard!');
            setShowAssetManager(false);
          }}
        />
      )}
    </div>
  );
};